import { BreakpointObserver, Breakpoints, MediaMatcher } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { fromEvent, map, merge, Observable, of, startWith } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class SrpPlatformService {
    screenUltraSmallBreakpoint = 414;
    screenSmallBreakpoint = 560;
    screenTabBreakpoint = 768;
    screenBigTabBreakpoint = 1024;
    screenMediumBreakpoint = 1280;
    screenLargeBreakpoint = 1920;
    toolbarLargeHeight = 90;
    toolbarMediumHeight = 60;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        @Inject(DOCUMENT) private _document: Document,
        private _media: MediaMatcher,
        private _breakpointObserver: BreakpointObserver,
    ) { }

    get isBrowser(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    get isServer(): boolean {
        return isPlatformServer(this.platformId);
    }

    get screenUltraSmall(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenUltraSmallBreakpoint;
    }

    screenUltraSmall$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenUltraSmallBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenSmall(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenSmallBreakpoint;
    }

    screenSmall$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenSmallBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenTab(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenTabBreakpoint;
    }

    screenTab$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenTabBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenBigTab(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenBigTabBreakpoint;
    }

    screenBigTab$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenBigTabBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenMedium(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth <= this.screenMediumBreakpoint;
    }

    screenMedium$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenMediumBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenLarge(): boolean {
        if (!this.isBrowser) {
            return true;
        }
        return (
            window.innerWidth <= this.screenLargeBreakpoint &&
            window.innerWidth > this.screenMediumBreakpoint
        );
    }

    screenLarge$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(true);
        }
        return this._breakpointObserver
            .observe(`(min-width: ${this.screenMediumBreakpoint}px) and (max-width: ${this.screenLargeBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenExtraLarge(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth > this.screenLargeBreakpoint;
    }

    screenExtraLarge$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(min-width: ${this.screenLargeBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get screenSmallLarge(): boolean {
        if (!this.isBrowser) {
            return false;
        }
        return window.innerWidth < this.screenLargeBreakpoint;
    }

    screenSmallLarge$(): Observable<boolean> {
        if (!this.isBrowser) {
            return of(false);
        }
        return this._breakpointObserver
            .observe(`(max-width: ${this.screenLargeBreakpoint}px)`)
            .pipe(map(x => x.matches));
    }

    get isLandscapeMode(): boolean {
        return this._media.matchMedia('(orientation: landscape)').matches;
    }

    isLandscapeMode$(): Observable<boolean> {
        return this._breakpointObserver
            .observe([
                Breakpoints.HandsetLandscape,
                Breakpoints.TabletLandscape,
                Breakpoints.WebLandscape,
            ])
            .pipe(map(x => x.matches));
    }

    get isTouchScreen(): boolean {
        if (!this.isBrowser) return false;
        
        // Check for iOS devices via User Agent (important for Chrome on iOS)
        const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                          (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 0);
        
        // Check for Android devices
        const isAndroidDevice = /Android/i.test(navigator.userAgent);
        
        // iOS and Android are always touch screen devices (even with external mouse)
        if (isIosDevice || isAndroidDevice) {
            return true;
        }
        
        // CRITICAL: Use 'any-pointer' and 'any-hover' instead of 'pointer' and 'hover'!
        // 
        // 'pointer: fine' checks PRIMARY input only - on hybrid devices (touch screen monitors,
        // all-in-ones like iMac/Surface Studio) the PRIMARY might be touch even though mouse exists.
        //
        // 'any-pointer: fine' checks if ANY pointing device is fine (mouse/trackpad).
        // This correctly detects hybrid devices that have BOTH touch AND mouse.
        //
        // Examples:
        // - iPad with no mouse:      any-pointer: fine = FALSE
        // - Android tablet:          any-pointer: fine = FALSE  
        // - Touch monitor + mouse:   any-pointer: fine = TRUE  ← This is the fix!
        // - Surface with mouse:      any-pointer: fine = TRUE
        // - Regular desktop:         any-pointer: fine = TRUE
        //
        const hasAnyFinePointer = this._media.matchMedia('(any-pointer: fine)').matches;
        const hasAnyHoverCapability = this._media.matchMedia('(any-hover: hover)').matches;
        
        // If device has ANY fine pointer (mouse exists), treat as desktop
        if (hasAnyFinePointer && hasAnyHoverCapability) {
            return false;
        }
        
        // Check multiple touch detection methods for pure touch devices (no mouse)
        const hasTouchStart = 'ontouchstart' in window;
        const hasMsTouchPoints = navigator['msMaxTouchPoints'] > 0;
        const hasMaxTouchPoints = navigator.maxTouchPoints > 0;
        
        // Media query for coarse pointer (touch devices)
        const hasCoarsePointer = this._media.matchMedia('(pointer: coarse)').matches;
        
        return hasTouchStart || hasMsTouchPoints || hasMaxTouchPoints || hasCoarsePointer;
    }

    get isSupportVibration(): boolean {
        return 'vibrate' in navigator;
    }

    /**
     * Detect Safari 15-17.3 which has a bug with round(calc(...)) in CSS.
     * Safari 17.4+ (iOS 17.4+) fixed this issue.
     * 
     * Bug: Safari claims to support round() via @supports, but fails on complex nested calc().
     * Example: round(down, calc(min(1, calc(...)) * var(...)), 1px) renders as 48px instead of correct value.
     * 
     * Note: All browsers on iOS use WebKit, so this affects Chrome/Firefox/Edge on iOS too.
     */
    get isLegacySafari(): boolean {
        if (!this.isBrowser) return false;
        
        const ua = navigator.userAgent;
        
        // iOS/iPadOS devices (all browsers on iOS use Safari's WebKit engine)
        // Pattern 1: Traditional iOS user agent with "OS X_Y"
        const iosMatch = ua.match(/OS (\d+)_(\d+)?/);
        if (iosMatch) {
            const major = parseInt(iosMatch[1]);
            // iOS 15.x-17.x: broken CSS var() with complex round(calc(...)) formulas
            if (major >= 15 && major <= 17) {
                return true;
            }
        }
        
        // Pattern 2: iPadOS 13+ masquerading as Mac (reports "Macintosh" + "Mac OS X")
        // These iPads request desktop sites by default and hide their identity
        // Detection: Mac user agent + touch support + Safari
        const isMacUA = /Macintosh/.test(ua) && /Mac OS X/.test(ua);
        const isSafari = /Safari/.test(ua) && !/Chrome/.test(ua) && !/Chromium/.test(ua);
        const hasTouch = navigator.maxTouchPoints > 0;
        
        if (isMacUA && isSafari && hasTouch) {
            // This is likely an iPad masquerading as Mac
            // Extract Safari version from UA (e.g., "Version/16.0 Safari/605.1.15")
            const safariVersionMatch = ua.match(/Version\/(\d+)\.(\d+)/);
            if (safariVersionMatch) {
                const safariMajor = parseInt(safariVersionMatch[1]);
                // Safari 15-17 on iPad has the same round() bugs
                if (safariMajor >= 15 && safariMajor <= 17) {
                    return true;
                }
            }
        }
        
        return false;
    }

    /**
     * Legacy mobile devices with limited CSS calc() support:
     * - iOS 15-17 (all browsers): broken CSS var() with complex round(calc(...)) formulas
     * - Android (all versions): cannot handle complex nested calc() with round()
     */
    get isLegacyMobile(): boolean {
        if (!this.isBrowser) return false;
        
        // Include legacy Safari (iOS and iPadOS)
        if (this.isLegacySafari) return true;
        
        // Android devices (all versions have issues with complex calc formulas)
        const ua = navigator.userAgent;
        const platform = navigator.platform;
        
        // Check UA for Android
        if (/Android/.test(ua)) return true;
        
        // BrowserStack and some emulators don't report Android in UA
        // but report Linux ARM platform (mobile architecture)
        if (/Linux arm/i.test(platform) && navigator.maxTouchPoints > 0) return true;
        
        return false;
    }

    get isIphone(): boolean {
        return /iPhone/.test(navigator.platform);
    }

    get isIos(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    }

    get isNewIPadOs(): boolean {
        return /Macintosh/.test(navigator.userAgent) && this.isTouchScreen;
    }

    get isAndroid(): boolean {
        return /Android/i.test(navigator.userAgent);
    }

    get isSafari(): boolean {
        return /^((?!chrome).)*safari.*$/i.test(navigator.userAgent);
    }

    get isChromium(): boolean {
        return /Chrome/.test(navigator.userAgent);
    }

    get isFirefox(): boolean {
        return /Firefox\/([0-9]+)\./i.test(navigator.userAgent);
    }

    get innerHeight(): number {
        if (!this.isBrowser) {
            return <any>'auto';
        }
        const toolbarHeight = this.screenMedium ? this.toolbarMediumHeight : this.toolbarLargeHeight;
        return window.innerHeight - toolbarHeight;
    }

    innerHeight$(): Observable<number | 'auto'> {
        if (!this.isBrowser) {
            return of(<any>'auto');
        }
        // Observe window resize and orientation change events
        const resize$ = fromEvent(this._document.defaultView!, 'resize');
        const orientationChange$ = fromEvent(this._document.defaultView!, 'orientationchange');
        
        return merge(resize$, orientationChange$).pipe(
            startWith(null), // Emit initial value
            map(() => {
                const isScreenMedium = window.innerWidth <= this.screenMediumBreakpoint;
                const toolbarHeight = isScreenMedium ? this.toolbarMediumHeight : this.toolbarLargeHeight;
                return window.innerHeight - toolbarHeight;
            })
        );
    }

    get innerWidth(): number {
        return window.innerWidth;
    }

    innerWidth$(): Observable<number> {
        if (!this.isBrowser) {
            return of(0);
        }
        // Observe window resize and orientation change events
        const resize$ = fromEvent(this._document.defaultView!, 'resize');
        const orientationChange$ = fromEvent(this._document.defaultView!, 'orientationchange');
        
        return merge(resize$, orientationChange$).pipe(
            startWith(null), // Emit initial value
            map(() => window.innerWidth)
        );
    }

    // Legacy browsers object for backward compatibility
    browsers = {
        get isIPhone() { return /iPhone/.test(navigator.platform); },
        get isIOs() { return /iPad|iPhone|iPod/.test(navigator.userAgent); },
        isNewIPadOs: /Macintosh/.test(navigator.userAgent) && this.isTouchScreen,
        get isAndroid() { return /Android/i.test(navigator.userAgent); },
        get isSafari() { return /^((?!chrome).)*safari.*$/i.test(navigator.userAgent); },
        get isChromium() { return /Chrome/.test(navigator.userAgent); },
        get isFirefox() { return /Firefox\/([0-9]+)\./i.test(navigator.userAgent); },
    };
}
